/**
 * Regras de ordem do tenant (spec 2026-09-30 §6.2/§6.3, TASK-12120).
 *
 * A regra não é privada: quem tem `host_order:ver_regras` vê todas as do tenant,
 * com dono, escopo, estado, validade e o que cada uma já fez. Pausar, retomar e
 * revogar aparecem para quem o servidor disse que pode (`pode_administrar`: dono
 * ou Gestor do tenant) — e o servidor decide de novo no clique.
 *
 * Não há criação aqui: a regra só nasce de uma ordem concluída, no cartão do
 * resultado no chat. Nem edição: mudar o escopo é revogar e criar outra (§6.3).
 */
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, Badge, Button, Group, Modal, Stack, Table, Text } from '@mantine/core'
import { IconAlertTriangle, IconRepeat } from '@tabler/icons-react'

import { DataCard, EmptyState } from '@/components/ui'
import {
  fraseDaRecusa, listarRegras, mudarEstadoRegra,
  type AcaoDaRegra, type EstadoDaRegra, type RegraDeOrdem,
} from '@/api/regrasDeOrdem'
import { FONTE_DADO } from '@/pages/chat/components/Dado'

const ESTADO: Record<EstadoDaRegra, { rotulo: string; cor: string }> = {
  ativa: { rotulo: 'Ativa', cor: 'inovareTeal' },
  pausada: { rotulo: 'Pausada', cor: 'yellow' },
  revogada: { rotulo: 'Revogada', cor: 'gray' },
}

/** Estado que a tela não conhece (o servidor ganhou um valor novo): neutro e sem ação —
 * chamá-lo de "Revogada" afirmaria o que não se sabe. */
const ESTADO_DESCONHECIDO = { rotulo: 'Estado desconhecido', cor: 'gray' }

/** `motivo_estado` em frase. Motivo sem frase não aparece: o código cru não vai à tela. */
const MOTIVO: Record<string, string> = {
  pausar_por_usuario: 'pausada à mão',
  revogar_por_usuario: 'revogada à mão',
  execucao_nao_limpa: 'o disco voltou a encher nas 24 h depois de uma limpeza',
  execucao_falhou: 'uma limpeza terminou em erro',
  dono_sem_permissao: 'o dono perdeu a permissão de diagnosticar neste ambiente',
  dono_sem_acesso_a_conversa: 'o dono perdeu o acesso à conversa do alerta',
}

const ACOES: Record<EstadoDaRegra, AcaoDaRegra[]> = {
  ativa: ['pausar', 'revogar'],
  pausada: ['retomar', 'revogar'],
  revogada: [],
}

const ROTULO_ACAO: Record<AcaoDaRegra, string> = {
  pausar: 'Pausar',
  retomar: 'Retomar',
  revogar: 'Revogar',
}

function data(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR')
}

function vencida(iso: string): boolean {
  const d = new Date(iso)
  return !Number.isNaN(d.getTime()) && d.getTime() <= Date.now()
}

const dado = { fontFamily: FONTE_DADO }

/** O que a regra faz, do que o GET já devolve: montagem ≥ limiar, verbos e teto. Campo
 * ausente não vira valor: a linha some. */
function Escopo({ regra }: { regra: RegraDeOrdem }) {
  return (
    <Stack gap={2} align="flex-start" data-escopo>
      {regra.montagem && (
        <Text size="sm" style={dado}>
          {regra.montagem}{regra.limiar_pct != null ? ` ≥ ${regra.limiar_pct}%` : ''}
        </Text>
      )}
      {regra.verbos && regra.verbos.length > 0 && (
        <Text size="xs" style={dado}>{regra.verbos.join(', ')}</Text>
      )}
      {regra.teto_semana != null && (
        <Text size="xs" c="dimmed">
          até <Text span size="xs" style={dado}>{regra.teto_semana}</Text> por semana
        </Text>
      )}
    </Stack>
  )
}

export function RegrasDeOrdemTabela({
  regras,
  onAcao,
  emAndamento,
}: {
  regras: RegraDeOrdem[]
  onAcao: (regra: RegraDeOrdem, acao: AcaoDaRegra) => void
  /** `${id}:${acao}` do clique que está no servidor agora. */
  emAndamento?: string | null
}) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <Table highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Regra</Table.Th>
            <Table.Th>Hosts</Table.Th>
            <Table.Th>Escopo</Table.Th>
            <Table.Th>Dono</Table.Th>
            <Table.Th>Estado</Table.Th>
            <Table.Th>Válida até</Table.Th>
            <Table.Th>Limpas</Table.Th>
            <Table.Th>Não limpas</Table.Th>
            <Table.Th>Aguardando 24 h</Table.Th>
            <Table.Th>Ações</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {regras.map((r) => {
            const e = ESTADO[r.estado] ?? ESTADO_DESCONHECIDO
            const motivo = r.motivo_estado ? MOTIVO[r.motivo_estado] : undefined
            // Vencida não se retoma (o servidor recusa com `regra_vencida`): o botão que só
            // pode falhar não aparece.
            const acoes = (r.pode_administrar ? ACOES[r.estado] ?? [] : [])
              .filter((a) => !(a === 'retomar' && vencida(r.valida_ate)))
            return (
              <Table.Tr key={r.id}>
                <Table.Td><Text size="sm" style={dado}>#{r.id}</Text></Table.Td>
                <Table.Td><Text size="sm" style={dado}>{r.host_padrao}</Text></Table.Td>
                <Table.Td><Escopo regra={r} /></Table.Td>
                <Table.Td><Text size="sm">{r.dono_nome}</Text></Table.Td>
                <Table.Td>
                  <Stack gap={2} align="flex-start">
                    <Badge color={e.cor} variant="light">{e.rotulo}</Badge>
                    {motivo && <Text size="xs" c="dimmed">{motivo}</Text>}
                  </Stack>
                </Table.Td>
                <Table.Td>
                  <Stack gap={2} align="flex-start">
                    <Text size="sm" style={dado}>{data(r.valida_ate)}</Text>
                    {r.estado !== 'revogada' && vencida(r.valida_ate) && (
                      <Text size="xs" c="dimmed">vencida</Text>
                    )}
                  </Stack>
                </Table.Td>
                <Table.Td><Text size="sm" style={dado}>{r.limpas}</Text></Table.Td>
                <Table.Td><Text size="sm" style={dado}>{r.nao_limpas}</Text></Table.Td>
                <Table.Td><Text size="sm" style={dado}>{r.pendentes}</Text></Table.Td>
                <Table.Td>
                  {acoes.length > 0 && (
                    <Group gap={6} wrap="nowrap">
                      {acoes.map((a) => (
                        <Button
                          key={a}
                          size="compact-xs"
                          variant="light"
                          color={a === 'revogar' ? 'red' : 'inovareTeal'}
                          loading={emAndamento === `${r.id}:${a}`}
                          disabled={!!emAndamento && emAndamento !== `${r.id}:${a}`}
                          onClick={() => onAcao(r, a)}
                        >
                          {ROTULO_ACAO[a]}
                        </Button>
                      ))}
                    </Group>
                  )}
                </Table.Td>
              </Table.Tr>
            )
          })}
        </Table.Tbody>
      </Table>
    </div>
  )
}

export default function RegrasDeOrdem() {
  const { tenantId: bruto } = useParams()
  const tenantId = Number(bruto)
  const qc = useQueryClient()
  const chave = ['regras-de-ordem', tenantId]
  const consulta = useQuery({
    queryKey: chave,
    queryFn: () => listarRegras(tenantId),
    enabled: Number.isInteger(tenantId) && tenantId > 0,
    retry: false,
  })
  const [aRevogar, setARevogar] = useState<RegraDeOrdem | null>(null)
  const [recusa, setRecusa] = useState<string | null>(null)

  const mudar = useMutation({
    mutationFn: ({ regra, acao }: { regra: RegraDeOrdem; acao: AcaoDaRegra }) =>
      mudarEstadoRegra(tenantId, regra.id, acao),
    onMutate: () => setRecusa(null),
    onSuccess: () => {
      setARevogar(null)
      void qc.invalidateQueries({ queryKey: chave })
    },
    onError: (erro) => {
      setARevogar(null)
      setRecusa(fraseDaRecusa(erro, 'mudar'))
      // A recusa mais comum é "mudou enquanto você olhava": a lista nova é a resposta.
      void qc.invalidateQueries({ queryKey: chave })
    },
  })
  const emAndamento = mudar.isPending && mudar.variables
    ? `${mudar.variables.regra.id}:${mudar.variables.acao}`
    : null

  const onAcao = (regra: RegraDeOrdem, acao: AcaoDaRegra) => {
    if (acao === 'revogar') setARevogar(regra)
    else mudar.mutate({ regra, acao })
  }

  const corpo = () => {
    if (!Number.isInteger(tenantId) || tenantId <= 0) {
      return <Text size="sm">Endereço sem ambiente válido.</Text>
    }
    if (consulta.isPending) return <Text size="sm" c="dimmed">Carregando as regras…</Text>
    if (consulta.isError) {
      return (
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
          <Text size="sm">{fraseDaRecusa(consulta.error, 'listar')}</Text>
        </Alert>
      )
    }
    if (!consulta.data.length) {
      return (
        <EmptyState
          bare
          size="md"
          icon={IconRepeat}
          title="Nenhuma regra de ordem neste ambiente"
          description="Uma regra nasce de uma limpeza de disco que funcionou: no cartão do resultado, no chat, quem pediu a ordem clica em Criar regra a partir desta ordem."
        />
      )
    }
    return <RegrasDeOrdemTabela regras={consulta.data} onAcao={onAcao} emAndamento={emAndamento} />
  }

  return (
    <Stack gap="md">
      {/* Sem PageHeader próprio: título e trilha vêm do AppLayout (routeRegistry). */}
      <DataCard title="Regras de ordem">
        <Stack gap="sm">
          <Text size="xs" c="dimmed" style={{ maxWidth: 860 }}>
            Cada regra prepara o laudo quando o alerta de disco volta nos hosts que ela cobre. A
            limpeza continua escolhida pelo dono e aprovada pelo time. Limpas e não limpas contam o
            que aconteceu nas 24 h depois de cada limpeza; uma não limpa pausa a regra.
          </Text>
          {recusa && (
            <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}
                   withCloseButton onClose={() => setRecusa(null)}>
              <Text size="sm">{recusa}</Text>
            </Alert>
          )}
          {corpo()}
        </Stack>
      </DataCard>

      <Modal opened={aRevogar != null} onClose={() => setARevogar(null)} title="Revogar a regra">
        <Stack gap="sm">
          <Text size="sm">
            Revogar a regra{' '}
            <Text span size="sm" fw={600} style={dado}>#{aRevogar?.id}</Text>
            {' '}de{' '}
            <Text span size="sm" fw={600} style={dado}>{aRevogar?.host_padrao}</Text>
            ? Revogada não volta: para ter a regra de novo, é preciso criar outra a partir de uma
            ordem nova.
          </Text>
          <Group gap="xs" justify="flex-start">
            <Button
              color="red"
              loading={mudar.isPending}
              onClick={() => aRevogar && mudar.mutate({ regra: aRevogar, acao: 'revogar' })}
            >
              Revogar
            </Button>
            <Button variant="default" onClick={() => setARevogar(null)}>Cancelar</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
